import { useNavigate, useParams } from "@tanstack/react-router"
import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface Stage {
  id: string
  name: string
  orderIndex: number
  entryConditions: any
  exitConditions: any
  messageTemplates: any[]
  aiInstructions: string
  maxAttempts: number
  followUpDelay?: number
  isActive: boolean
}

interface Campaign {
  id: string
  name: string
  description?: string
  status: "draft" | "active" | "paused" | "completed"
  settings: any
  targetMetrics?: any
  campaignContext?: string
  objectives?: string[]
  stages: Stage[]
  conversations?: {
    id: string
    contact?: {
      name?: string
      email?: string
      phone?: string
    }
    status: string
    platform: string
  }[]
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors = {
    draft: "bg-foregroundx text-gray-800  dark:text-gray-300",
    active: "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-300",
    paused: "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-300",
    completed: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-300",
  }

  return (
    <span
      className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status as keyof typeof colors] || colors.draft}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

const CampaignDetail: React.FC = () => {
  const params = useParams({ from: "/crm/campaigns/$id" })
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<"overview" | "stages" | "settings" | "conversations">("overview")
  const [editingStage, setEditingStage] = useState<Stage | null>(null)
  const [showStageModal, setShowStageModal] = useState(false)

  const campaignId = params.id

  // Query campaign details
  const {
    data: campaign,
    isLoading,
    refetch,
  } = trpc.crm.campaigns.getById.useQuery({ id: campaignId }, { enabled: !!campaignId })

  // Query campaign analytics
  const { data: analytics } = trpc.crm.campaigns.getAnalytics.useQuery({ campaignId }, { enabled: !!campaignId })

  // Update campaign mutation
  const updateCampaignMutation = trpc.crm.campaigns.update.useMutation({
    onSuccess: () => {
      void refetch()
    },
  })

  // Create stage mutation
  const createStageMutation = trpc.crm.stages.create.useMutation({
    onSuccess: () => {
      setShowStageModal(false)
      setEditingStage(null)
      void refetch()
    },
  })

  // Update stage mutation
  const updateStageMutation = trpc.crm.stages.update.useMutation({
    onSuccess: () => {
      setShowStageModal(false)
      setEditingStage(null)
      void refetch()
    },
  })

  const handleUpdateCampaign = (data: Partial<Campaign>) => {
    updateCampaignMutation.mutate({
      id: campaignId,
      data,
    })
  }

  const handleCreateStage = (formData: FormData) => {
    const name = formData.get("name") as string
    const stageObjective = formData.get("stageObjective") as string
    const aiInstructions = formData.get("aiInstructions") as string
    const maxAttempts = parseInt(formData.get("maxAttempts") as string) || 3
    const followUpDelay = parseInt(formData.get("followUpDelay") as string) || undefined

    if (!name || !stageObjective || !aiInstructions) return

    createStageMutation.mutate({
      campaignId,
      name,
      orderIndex: campaign?.stages?.length || 0,
      stageObjective,
      aiInstructions,
      maxAttempts,
      followUpDelay,
      dataToCollect: [],
      transitionCriteria: undefined,
      exampleMessages: [],
    })
  }

  const handleUpdateStage = (formData: FormData) => {
    if (!editingStage) return

    const name = formData.get("name") as string
    const aiInstructions = formData.get("aiInstructions") as string
    const maxAttempts = parseInt(formData.get("maxAttempts") as string) || 3
    const followUpDelay = parseInt(formData.get("followUpDelay") as string) || undefined

    updateStageMutation.mutate({
      id: editingStage.id,
      data: {
        name,
        aiInstructions,
        maxAttempts,
        followUpDelay,
      },
    })
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-foregroundx ">
        <div className="text-gray-500">Loading campaign...</div>
      </div>
    )
  }

  if (!campaign) {
    return (
      <div className="h-full flex items-center justify-center bg-foregroundx ">
        <div className="text-center">
          <div className="text-gray-500 mb-4">Campaign not found</div>
          <button
            onClick={() => navigate({ to: "/crm/campaigns" })}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Campaigns
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto bg-foregroundx ">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <button
                  onClick={() => navigate({ to: "/crm/campaigns" })}
                  className=" hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  ← Back
                </button>
                <h1 className="text-3xl font-bold  dark:text-white">{campaign.name}</h1>
                <StatusBadge status={campaign.status} />
              </div>
              {campaign.description && <p className=" dark:text-gray-400">{campaign.description}</p>}
            </div>
            <div className="flex gap-2">
              {campaign.status === "draft" && (
                <button
                  onClick={() => handleUpdateCampaign({ status: "active" })}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  Activate
                </button>
              )}
              {campaign.status === "active" && (
                <button
                  onClick={() => handleUpdateCampaign({ status: "paused" })}
                  className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700"
                >
                  Pause
                </button>
              )}
              {campaign.status === "paused" && (
                <button
                  onClick={() => handleUpdateCampaign({ status: "active" })}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  Resume
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-border dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {[
                { key: "overview", label: "Overview", icon: "📊" },
                { key: "stages", label: "Stages", icon: "🔄" },
                { key: "settings", label: "Settings", icon: "⚙️" },
                { key: "conversations", label: "Conversations", icon: "💬" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${
                    activeTab === tab.key
                      ? "border-blue-500  dark:text-blue-400"
                      : "border-transparent text-gray-500 hover:text-primary hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Key Metrics */}
            {analytics && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-background  rounded-lg p-4 border border-border dark:border-gray-700">
                  <div className="text-sm  dark:text-gray-400">Active Conversations</div>
                  <div className="text-2xl font-semibold  dark:text-white">{analytics.activeConversations}</div>
                </div>
                <div className="bg-background  rounded-lg p-4 border border-border dark:border-gray-700">
                  <div className="text-sm  dark:text-gray-400">Total Messages</div>
                  <div className="text-2xl font-semibold  dark:text-white">{analytics.totalMessages}</div>
                </div>
                <div className="bg-background  rounded-lg p-4 border border-border dark:border-gray-700">
                  <div className="text-sm  dark:text-gray-400">Response Rate</div>
                  <div className="text-2xl font-semibold  dark:text-white">
                    {Math.round(analytics.responseRate * 100)}%
                  </div>
                </div>
                <div className="bg-background  rounded-lg p-4 border border-border dark:border-gray-700">
                  <div className="text-sm  dark:text-gray-400">Avg Messages/Conv</div>
                  <div className="text-2xl font-semibold  dark:text-white">
                    {Math.round(analytics.averageMessagesPerConversation)}
                  </div>
                </div>
              </div>
            )}

            {/* Campaign Info */}
            <div className="bg-background  rounded-lg p-6 border border-border dark:border-gray-700">
              <h3 className="text-lg font-semibold  dark:text-white mb-4">Campaign Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                    Campaign Context
                  </label>
                  <div className=" dark:text-white">{campaign.campaignContext || "No context provided"}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Stages Count</label>
                  <div className=" dark:text-white">{campaign.stages?.length || 0}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "stages" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold  dark:text-white">Campaign Stages</h3>
              <button
                onClick={() => {
                  setEditingStage(null)
                  setShowStageModal(true)
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <span>+</span>
                Add Stage
              </button>
            </div>

            {campaign.stages && campaign.stages.length > 0 ? (
              <div className="space-y-4">
                {campaign.stages
                  .sort((a, b) => a.orderIndex - b.orderIndex)
                  .map((stage, index) => (
                    <div
                      key={stage.id}
                      className="bg-background  rounded-lg p-6 border border-border dark:border-gray-700"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-300 text-xs font-medium px-2 py-1 rounded-full">
                              Stage {index + 1}
                            </span>
                            <h4 className="font-medium  dark:text-white">{stage.name}</h4>
                            {!stage.isActive && (
                              <span className="bg-foregroundx text-gray-800  dark:text-gray-300 text-xs font-medium px-2 py-1 rounded-full">
                                Inactive
                              </span>
                            )}
                          </div>

                          <div className="text-sm  dark:text-gray-400 mb-3">
                            {stage.aiInstructions || "No AI instructions set"}
                          </div>

                          <div className="flex gap-4 text-xs text-gray-500">
                            <span>Max attempts: {stage.maxAttempts}</span>
                            {stage.followUpDelay && <span>Follow-up delay: {stage.followUpDelay}h</span>}
                            <span>Templates: {stage.messageTemplates?.length || 0}</span>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setEditingStage(stage as any)
                              setShowStageModal(true)
                            }}
                            className=" hover:text-blue-800 text-sm px-3 py-1 border border-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-background  rounded-lg border border-border dark:border-gray-700">
                <div className="text-gray-500 mb-4">No stages configured</div>
                <button
                  onClick={() => {
                    setEditingStage(null)
                    setShowStageModal(true)
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Add First Stage
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === "settings" && (
          <div className="bg-background  rounded-lg p-6 border border-border dark:border-gray-700">
            <h3 className="text-lg font-semibold  dark:text-white mb-4">Campaign Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">AI Model</label>
                {/* eslint-disable-next-line @aix/no-hardcoded-model-strings -- legacy model, no enum available */}
                <div className=" dark:text-white">{(campaign.settings as any)?.aiModel || "gpt-4"}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                  Max Conversations Per Day
                </label>
                <div className=" dark:text-white">{(campaign.settings as any)?.maxConversationsPerDay || 100}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Working Hours</label>
                <div className=" dark:text-white">
                  {(campaign.settings as any)?.workingHours?.start || "09:00"} -{" "}
                  {(campaign.settings as any)?.workingHours?.end || "18:00"} (
                  {(campaign.settings as any)?.workingHours?.timezone || "America/Los_Angeles"})
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "conversations" && (
          <div className="bg-background  rounded-lg border border-border dark:border-gray-700">
            <div className="p-6 border-b border-border dark:border-gray-700">
              <h3 className="text-lg font-semibold  dark:text-white">Recent Conversations</h3>
            </div>

            {campaign.conversations && campaign.conversations.length > 0 ? (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {campaign.conversations.slice(0, 10).map((conversation) => (
                  <div key={conversation.id} className="p-4  dark:hover:bg-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium  dark:text-white">
                          {conversation.contact?.name ||
                            conversation.contact?.email ||
                            conversation.contact?.phone ||
                            "Unknown Contact"}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {conversation.platform} • {conversation.status}
                        </div>
                      </div>
                      <button
                        onClick={() => navigate({ to: `/crm/inbox/${conversation.id}` })}
                        className=" hover:text-blue-800 text-sm"
                      >
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="text-gray-500">No conversations yet</div>
              </div>
            )}
          </div>
        )}

        {/* Stage Modal */}
        {showStageModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background  rounded-lg p-6 w-full max-w-2xl">
              <h2 className="text-xl font-semibold  dark:text-white mb-4">
                {editingStage ? "Edit Stage" : "Create New Stage"}
              </h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  const formData = new FormData(e.currentTarget)
                  if (editingStage) {
                    handleUpdateStage(formData)
                  } else {
                    handleCreateStage(formData)
                  }
                }}
              >
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Stage Name</label>
                    <input
                      name="name"
                      type="text"
                      required
                      defaultValue={editingStage?.name || ""}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="Enter stage name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                      Stage Objective
                    </label>
                    <input
                      name="stageObjective"
                      type="text"
                      required
                      defaultValue={(editingStage as any)?.stageObjective || ""}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="What is this stage trying to achieve?"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                      AI Instructions
                    </label>
                    <textarea
                      name="aiInstructions"
                      rows={4}
                      required
                      defaultValue={editingStage?.aiInstructions || ""}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="Natural language instructions for the AI in this stage"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                        Max Attempts
                      </label>
                      <input
                        name="maxAttempts"
                        type="number"
                        min="1"
                        max="10"
                        defaultValue={editingStage?.maxAttempts || 3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                        Follow-up Delay (hours)
                      </label>
                      <input
                        name="followUpDelay"
                        type="number"
                        min="0"
                        defaultValue={editingStage?.followUpDelay || ""}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowStageModal(false)
                      setEditingStage(null)
                    }}
                    className="px-4 py-2  dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createStageMutation.isLoading || updateStageMutation.isLoading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createStageMutation.isLoading || updateStageMutation.isLoading
                      ? "Saving..."
                      : editingStage
                        ? "Update Stage"
                        : "Create Stage"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CampaignDetail
